import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as GatsbyLink } from 'gatsby';
import {
  Link, Drawer, IconProps, Badge,
} from '@printdeal/ui-library';
import { useDispatch, useSelector } from 'react-redux';
import {
  FeatureConfig,
  useStoreToggle,
} from '@printdeal/feature-management/react';
import { useSegmentation, isCustomerProgramReseller } from '@printdeal/segmentation/react';
import useSystemPages from '../../../../hooks/useSystemPages';
// @ts-expect-error
import { selectors as customerSelectors } from '../../../../store/slices/customerSlice';
import { actions as authActions } from '../../../../store/slices/authSlice';
import {
  dispatchDataLayerEventAccountSlideOut,
  dispatchDataLayerEventAccountSlideOutLink,
} from '../../../../helpers/dataLayerHelpers/navigationDataLayerHelper';
import NewBadge from '../../../customer-programs/NewBadge';
import { getConfiguration } from '../../../../helpers/Configuration';
import CustomerProgramsBadge from '../../../customer-programs/CustomerProgramsBadge';
import { reOrderMessages } from '../../../re-order/helpers/reOrderHelper';

interface AccountNavigationItem {
  to: string;
  title: React.ReactNode;
  text: string;
  icon?: IconProps['icon'];
}

const LoggedInNavigation = () => {
  const intl = useIntl();
  const [accountMenuOpen, setAccountMenuOpen] = useState<boolean>(false);
  const [accountDashboardPage, customerProgramsReseller] = useSystemPages('accountDashboard', 'customerProgramsReseller');
  const accountDashboardPath = accountDashboardPage.path;
  const customerFirstName: string = useSelector(customerSelectors.getCustomerFirstName);
  const isCustomerCompany: boolean = useSelector(customerSelectors.isCustomerCompany);
  const customerGroupId: string = useSelector(customerSelectors.getCustomerGroupId);
  const dispatch = useDispatch();
  const { data: segmentationData } = useSegmentation();
  const isMandatesToggleEnabled = useStoreToggle('drukwerkdeal-nl').featureToggle.isEnabled;
  const configuration = getConfiguration();
  const isStoreToggleEnabled = useStoreToggle('drukwerkdeal-nl_printdeal-be').featureToggle.isEnabled;

  const handleOpenAccountMenu = useCallback((location?: 'button' | 'outside_modal') => {
    setAccountMenuOpen(true);
    dispatchDataLayerEventAccountSlideOut(true, location);
  }, [setAccountMenuOpen]);

  const handleCloseAccountMenu = useCallback((location?: 'button' | 'outside_modal') => {
    setAccountMenuOpen(false);
    dispatchDataLayerEventAccountSlideOut(false, location);
  }, [setAccountMenuOpen]);

  const handleLogout = useCallback(() => {
    dispatch(authActions.logout());
  }, [dispatch]);

  const accountNavigationItems: AccountNavigationItem[] = [{
    to: `${accountDashboardPath}/orders`,
    title: (<FormattedMessage id="header.account.orders.short" defaultMessage="Orders" />),
    text: 'orders',
    icon: 'orders',
  }, {
    to: `${accountDashboardPath}/../reorder`,
    title: (<FormattedMessage
      id={reOrderMessages.reorderNavigationLinkText.id}
      defaultMessage={reOrderMessages.reorderNavigationLinkText.defaultMessage}
    />),
    text: 'reorder',
    icon: 'repeat',
  }, {
    to: `${accountDashboardPath}/editmain`,
    title: (<FormattedMessage id="header.account.settings.short" defaultMessage="Settings" />),
    text: 'settings',
    icon: 'user',
  }, {
    to: `${accountDashboardPath}/addresses`,
    title: (<FormattedMessage id="header.account.addresses.short" defaultMessage="Addresses" />),
    text: 'addresses',
    icon: 'address-book',
  }, {
    to: `${accountDashboardPath}/invoices`,
    title: (<FormattedMessage id="header.account.invoices.short" defaultMessage="Invoices" />),
    text: 'invoices',
    icon: 'euro-sign',
  }, ...((isStoreToggleEnabled && isCustomerProgramReseller(
    segmentationData?.isReseller,
    segmentationData?.isPartOfCustomerProgram,
  )) ? [{
      to: customerProgramsReseller.path || accountDashboardPath,
      title: (<FormattedMessage id="header.account.customerPrograms.partnerPro.short" defaultMessage="PartnerPro" />),
      text: 'customerPrograms',
      icon: 'partner-discount',
    }] as AccountNavigationItem[] : []), {
    to: `${accountDashboardPath}/newsletter`,
    title: (<FormattedMessage id="header.account.newsletter.short" defaultMessage="Newsletter" />),
    text: 'newsletter',
    icon: 'envelope',
  }, {
    to: `${accountDashboardPath}/complaints`,
    title: (<FormattedMessage id="header.account.complaints.short" defaultMessage="Complaints" />),
    text: 'complaints',
    icon: 'complaints',
  }, ...(configuration.quote.enabled ? [{
    to: `${accountDashboardPath}/quoting`,
    title: (<FormattedMessage id="header.account.quoting.short" defaultMessage="Quotes" />),
    text: 'quotes',
    icon: 'file',
  }] as AccountNavigationItem[] : []), {
    to: `${accountDashboardPath}/designs`,
    title: (<FormattedMessage id="header.account.designs.short" defaultMessage="Designs" />),
    text: 'designs',
    icon: 'image',
  }, ...(isCustomerCompany && isMandatesToggleEnabled ? [{
    to: `${accountDashboardPath}/mandates`,
    title: (<FormattedMessage id="header.account.mandates.short" defaultMessage="Mandates" />),
    text: 'mandates',
    icon: 'euro-sign',
  }] as AccountNavigationItem[] : []), {
    to: `${accountDashboardPath}`,
    title: (<FormattedMessage id="header.account.title.short" defaultMessage="My account" />),
    text: 'my account',
    icon: 'user',
  }];

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        as="button"
        type="button"
        variant="secondary"
        iconBefore="user"
        onClick={() => handleOpenAccountMenu()}
        data-cy="headerAccountToggle"
        className="pd-flex"
      >
        <div className="pd-sr-only pd-flex-col lg:pd-not-sr-only">
          <FormattedMessage id="header.account.title" defaultMessage="My account" />
          {(
            isStoreToggleEnabled && isCustomerProgramReseller(
              segmentationData?.isReseller,
              segmentationData?.isPartOfCustomerProgram,
            ) && (
              <div className="pd-text-sm pd-font-normal pd-text-secondary-500">
                <FormattedMessage id="header.account.SegmentGroupLabel" defaultMessage="Partner Pro" />: {segmentationData?.category}
              </div>
            ))}
        </div>
      </Link>

      <Drawer
        isOpen={accountMenuOpen}
        position="right"
        onClose={() => handleCloseAccountMenu('outside_modal')}
      >
        <Drawer.Header
          title={(
            <span data-hj-suppress>
              <FormattedMessage id="header.account.welcome" defaultMessage="Welkom" />
              {customerFirstName ? ` ${customerFirstName}!` : '!'}
            </span>
          )}
          onClose={() => handleCloseAccountMenu('button')}
          dismissLabel={intl.formatMessage({
            id: 'account.general.modal.close',
            defaultMessage: 'Close',
          })}
        >
          <CustomerProgramsBadge />
        </Drawer.Header>
        <Drawer.Content>
          <ul>
            {accountNavigationItems
              .map((item, index: number) => {
                const {
                  title,
                  text,
                  to,
                  icon,
                } = item;
                const key = `account-nav-item-${index}`;
                return (
                  <li key={key} className="pd-border-b pd-px-3 pd-py-6 first:pd-pt-2">
                    <Link
                      as={GatsbyLink}
                      to={to}
                      variant="secondary"
                      iconBefore={icon}
                      onClick={() => {
                        setAccountMenuOpen(false);
                        dispatchDataLayerEventAccountSlideOutLink(text);
                      }}
                      className="pd-flex"
                    >
                      {title}
                      {text === 'customerPrograms' && <NewBadge variant="warning" />}
                    </Link>
                  </li>
                );
              })}
            <li className="pd-flex pd-px-3 pd-py-6">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                data-cy="headerLogoutButton"
                as="button"
                type="button"
                variant="secondary"
                onClick={handleLogout}
                className="pd-flex"
              >
                <FormattedMessage id="header.account.signout" defaultMessage="Logout" />
              </Link>
            </li>
          </ul>
          <FeatureConfig<{ groupId: string; groupName: string; }> id="segmented_group_config">
            {({ featureConfig }) => (
              featureConfig.variables.groupId === customerGroupId ? (
                <>
                  <Badge variant="warning">
                    <FormattedMessage
                      id="segmented.customer.dropdown.badge.resellersSegmentedPricing"
                      defaultMessage="Personal Discount"
                    />
                  </Badge>
                  <FormattedMessage
                    id="segmented.customer.dropdown.message.resellersSegmentedPricing"
                    defaultMessage="Your personal discounts is active and will be calculated automatically"
                  />
                </>
              ) : null
            )}
          </FeatureConfig>
        </Drawer.Content>
      </Drawer>
    </>
  );
};

export default LoggedInNavigation;
